<template>
  <section
    class="containerMyApps"
    :class="{'mobile': mobile}"
    id="myApps">
    <section class="containerMyApps__header">
      <span class="containerMyApps__header--label">My Apps</span>
      <span class="icon-grey-close containerMyApps__header--close" v-if="mobile" @click="closeMyApps"></span>
    </section>
    <section class="containerMyApps__noItemsMessage" v-if="appsLoaded && !apps.length">
      <span class="containerMyApps__noItemsMessage--label">No applications to display at this time</span>
    </section>
    <section v-else class="containerMyApps__instructionsMessage">
      <span class="containerMyApps__instructionsMessage--label">Drag and drop to rearrange your apps to your liking</span>
    </section>
    <section class="containerMyApps__list customScrollbar">
      <ul v-if="appsLoaded && !isPhone" class="containerMyApps__list--items">
        <li class="item"
          :id="`draggable${index}`"
          v-for="(item, index) in apps" :key="index"
          :class="{'hoverActive': indexOver === index}"
          @mouseover="indexOver = index"
          @mouseleave="indexOver = null"
          :draggable="!isPhone"
          @dragstart="dragStart($event, index)"
          @drag="drag($event)"
          @dragend="dragEnd($event, index)"
          @drop.prevent
          @dragenter="dragEnter($event, index)"
          @dragover.prevent
          @dragleave.prevent>
          <div class="item__appInfo" @click="openImageUrl(item)">
            <img class="item__appInfo--image" :src="appImageSrc(item)" />
            <div class="item__appInfo--label">
              <span class="icon icon-grey-drag"></span>
              <span class="label" style="-webkit-box-orient: vertical;">{{item.name}}</span>
            </div>
          </div>
        </li>
      </ul>
      <ul v-if="appsLoaded && isPhone" class="containerMyApps__list--itemsMobile">
        <draggable 
          v-model="apps" 
          group="people" 
          @end="dragEndMobile"
          @dragover.prevent
          ghost-class="none"
          item-key="id">
          <template #item="{element}">
            <li class="item">
              <div class="item__appInfo" @click="openImageUrl(element)">
                <div class="item__appInfo--image">
                  <img class="myAppImage" :src="appImageSrc(element)" />
                </div>
                <div class="item__appInfo--label">
                  <span class="label" style="-webkit-box-orient: vertical;">{{element.name}}</span>
                </div>
              </div>
            </li>
          </template>
        </draggable>
      </ul>
      <section class="containerMyApps__list--restore" v-if="appsLoaded && apps.length">
        <span class="label" @click="restoreOrder">Restore to default</span>
      </section>
    </section>
  </section>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { getMyApps, reorderMyApps, restoreOrderMyApps } from '../../services'
import draggable from 'vuedraggable'

export default {
  name: 'myApps',
  components: {
    draggable
  },
  props: {
    mobile: {
      type: Boolean,
      required: true
    },
    isPhone: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close-myapps-menu', 'go-back'],
  setup (props, { emit }) {
    const apps = ref([])
    const appsLoaded = ref(false)
    const dragging = ref(-1)
    const indexOver = ref(null)
    const handleObject = ref(null)
    const dragClickOffsetX = ref(0)
    const dragClickOffsetY = ref(0)
    const lastDragX = ref(0)
    const lastDragY = ref(0)
    const transparentDragImage = ref(null)

    onMounted(() => {
      getMyAppsList()
      createTransparentDragImage()
    })

    /**
     * @description Set card image src
     */
    const appImageSrc = computed(() => (item) => {
      return `data:image/png;base64,${item.icon}`
    })

    /**
     * @description Get My Apps list
     */
    function getMyAppsList () {
      getMyApps().then(
        (response) => {
          apps.value = response.data
          appsLoaded.value = true
        },
        (error) => {
          console.error(error)
        }
      )
    }

    /**
     * @description Close menu
     */
    function closeMyApps () {
      emit('close-myapps-menu')
    }

    /**
     * @description Go back apps menu
     */
    function goBack () {
      emit('go-back', 'myApps')
    }

    /**
     * @description open item url
     * @param item app item
     */
    function openImageUrl (item) {
      window.open(item.url, '_blank')
    }

    /**
     * @description create transparent image to replace default drag image 
     */
    function createTransparentDragImage () {
      transparentDragImage.value = document.createElement('img')
      transparentDragImage.value.src = 'data:image/png;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
    }

    /**
     * @description action when we start dragging a draggable element
     * @param event DOM event
     * @param index index of the element
     */
    function dragStart (event, index) {
      event.dataTransfer.dropEffect = 'move'
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setDragImage(transparentDragImage.value, 0, 0)
      const draggedObject = event.target
      handleObject.value = draggedObject.cloneNode(true)
      draggedObject.parentNode.insertBefore(handleObject.value, draggedObject)
      styleHandle(draggedObject.offsetWidth, draggedObject.offsetHeight)
      dragging.value = index
      dragClickOffsetX.value = event.offsetX
      dragClickOffsetY.value = event.offsetY
      document.querySelector(`#draggable${index}`).style.opacity = 0
    }

    /**
     * @description Initial styles for cloned object
     * @param width original object width
     * @param height original object height
     */
    function styleHandle (width, height) {
      handleObject.value.id = 'cloneObject'
      handleObject.value.style.position = 'fixed'
      handleObject.value.style.zIndex = 9999
      handleObject.value.style.width = width + 'px'
      handleObject.value.style.height = height + 'px'
      handleObject.value.style.left = '-9999px'
      handleObject.value.style['pointer-events'] = 'none'
      handleObject.value.style.backgroundColor = '#FFFFFF'
      handleObject.value.style.display = 'flex'
      handleObject.value.style.alignItems = 'center'

      handleObject.value.style.margin = 0
      handleObject.value.style.padding = 0
    }

    /**
     * @description action when we drag a draggable element
     * @param event DOM event
     */
    function drag (event) {
      let useX = event.x
      let useY = event.y
      if (useX === 0 && useY === 0) {
        useX = lastDragX.value
        useY = lastDragY.value
      }
      if (useX === lastDragX.value && useY === lastDragY.value) {
        return
      }
      translate(useX - dragClickOffsetX.value, useY - dragClickOffsetY.value)
      lastDragX.value = useX
      lastDragY.value = useY

      const rect = handleObject.value.getBoundingClientRect()
      const bottomSpace = window.innerHeight - rect.bottom
      if (bottomSpace < 20) {
        document.querySelector('.containerMyApps').scrollTop += 20
      }
      if (rect.top < 20) {
        document.querySelector('.containerMyApps').scrollTop -= 20
      }
    }

    /**
     * @description Translate cloned object with mouse moving
     * @param x x position to locate cloned object
     * @param y y position to locate cloned object
     */
    function translate (x, y) {
      handleObject.value.style.left = x + 'px'
      handleObject.value.style.top = y + 'px'
    }

    /**
     * @description action when we end dragging a draggable element
     * @param event DOM event
     * @param indexTo index of the element
     */
    function dragEnd (event, indexTo) {
      document.querySelector(`#draggable${indexTo}`).style.opacity = 1
      document.querySelector(`#draggable${dragging.value}`).style.opacity = 1
      if (indexTo !== dragging.value) {
        const orderedApps = apps.value.map(app => app.id)
        reorderMyApps({ apps: orderedApps })
      }
      handleObject.value.parentNode.removeChild(handleObject.value)
      handleObject.value = null
      dragging.value = -1
    }

    /**
     * @description action when we end dragging a draggable element in mobile mode
     * @param event DOM event
     */
    function dragEndMobile (event) {
      if (event.newIndex !== event.oldIndex) {
        const orderedApps = apps.value.map(app => app.id)
        reorderMyApps({ apps: orderedApps })
      }
    }

    /**
     * @description action when we enter with a draggable element on a droppable area
     * @param event DOM event
     * @param indexTo index of the element
     */
    function dragEnter (event, indexTo) {
      apps.value.splice(indexTo, 0, apps.value.splice(dragging.value, 1)[0])
      if (indexTo !== dragging.value) {
        document.querySelector(`#draggable${indexTo}`).style.opacity = 0
        document.querySelector(`#draggable${dragging.value}`).style.opacity = 1
      }
      indexOver.value = indexTo
      dragging.value = indexTo
    }

    /**
     * @description Restore my apps order to default
     */
    function restoreOrder () {
      restoreOrderMyApps().then(
        (response) => {
          apps.value = response.data
        },
        (error) => {
          console.error(error)
        }
      )
    }

    return {
      appImageSrc,
      closeMyApps,
      goBack,
      openImageUrl,
      dragStart,
      drag,
      dragEnd,
      dragEndMobile,
      dragEnter,
      restoreOrder,
      appsLoaded,
      indexOver,
      apps
    }
  }
}
</script>
